/**
 * Vendor product form (B17, D80). Pure: form ↔ product, validation, and the
 * patch an edit sends. An edit sends only what changed, because the server
 * sends any change but stock back to review (D80) — resending an unchanged
 * brand or spec would pull a live product off the storefront for nothing.
 */
import { formFromAttributes, attributesFromForm, validateAttributes } from '@/lib/productTypes';
import { rupeesToPaise, paiseToRupees } from '@/merchant/lib/vendors';
import { GRADES, conditionErrors, gradeRequired } from '@/lib/conditions';

/** The top category and subcategory ids for a category id, from the tree. */
export function splitCategory(tree = [], categoryId) {
  const id = categoryId == null ? null : Number(categoryId);
  for (const top of tree) {
    if (Number(top.id) === id) return { top: String(top.id), sub: '' };
    const child = (top.children || []).find((c) => Number(c.id) === id);
    if (child) return { top: String(top.id), sub: String(child.id) };
  }
  return { top: '', sub: '' };
}

/** The category the product files under: the subcategory when picked, else the top category. */
export function pickedCategory(tree = [], form) {
  const top = tree.find((t) => String(t.id) === String(form.top));
  if (!top) return null;
  const sub = (top.children || []).find((c) => String(c.id) === String(form.sub));
  return sub ? { ...sub, product_type: sub.product_type || top.product_type } : top;
}

export function emptyForm(tree = [], product = null, typeOf = () => null) {
  if (!product) return { top: '', sub: '', brand: '', model_name: '', title: '', price: '', mrp: '', qty: '1', warranty_months: '0', condition: '', grade: '', specs: {} };
  const { top, sub } = splitCategory(tree, product.category_id);
  return {
    top, sub, brand: product.brand || '', model_name: product.model_name || '', title: product.title || '',
    price: paiseToRupees(product.price_paise), mrp: paiseToRupees(product.mrp_paise), qty: String(product.qty_on_hand ?? 0),
    warranty_months: String(product.warranty_months ?? 0), condition: product.condition || '', grade: product.grade || '',
    specs: formFromAttributes(typeOf(product.product_type), product.attributes || {}),
  };
}

/**
 * { field: message } for the form; `type` is the picked category's product type and
 * `conditions` the registry (useProductTypes().conditions): no default condition, and a
 * grade only where the condition needs one (D98).
 */
export function validateForm(form, type, conditions = []) {
  const e = {};
  if (!form.top) e.top = 'Pick a category';
  if (!String(form.brand).trim()) e.brand = 'Enter the brand';
  if (!String(form.model_name).trim()) e.model_name = 'Enter the model';
  if (String(form.title).trim().length < 3) e.title = 'At least 3 characters';
  const price = rupeesToPaise(form.price);
  if (price == null) e.price = 'Enter the selling price';
  if (String(form.mrp).trim() !== '') {
    const mrp = rupeesToPaise(form.mrp);
    if (mrp == null) e.mrp = 'Enter a valid MRP';
    else if (price != null && mrp < price) e.mrp = 'MRP cannot be below the price';
  }
  const qty = Number(form.qty);
  if (!Number.isInteger(qty) || qty < 0 || qty > 100000) e.qty = 'Whole number from 0';
  const w = Number(form.warranty_months || 0);
  if (!Number.isInteger(w) || w < 0 || w > 120) e.warranty_months = '0 to 120 months';
  Object.assign(e, conditionErrors(form, { conditions, type, grades: GRADES }));
  const specs = validateAttributes(type, form.specs);
  if (Object.keys(specs).length) e.specs = specs;
  return e;
}

/** The full create payload. The grade goes only where the condition has one (when the registry is given). */
export function createPayload(form, category, type, conditions = null) {
  const grade = conditions && !gradeRequired(conditions, form.condition) ? null : form.grade || null;
  return {
    category_id: category.id, brand: form.brand.trim(), model_name: form.model_name.trim(), title: form.title.trim(),
    price_paise: rupeesToPaise(form.price), mrp_paise: String(form.mrp).trim() === '' ? null : rupeesToPaise(form.mrp),
    qty: Number(form.qty), warranty_months: Number(form.warranty_months || 0), condition: form.condition, grade,
    attributes: attributesFromForm(type, form.specs),
  };
}

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Only the changed fields of an edit, plus its id. `{ id }` alone means nothing changed. */
export function editPatch(product, form, category, type, conditions = null) {
  const next = createPayload(form, category, type, conditions);
  const before = {
    category_id: product.category_id, brand: product.brand, model_name: product.model_name, title: product.title,
    price_paise: product.price_paise, mrp_paise: product.mrp_paise, qty: product.qty_on_hand, warranty_months: product.warranty_months,
    condition: product.condition, grade: product.grade || null, attributes: attributesFromForm(type, formFromAttributes(type, product.attributes || {})),
  };
  const patch = { id: product.id };
  for (const k of Object.keys(next)) {
    const changed = k === 'attributes' ? !same(next[k], before[k]) : String(next[k] ?? '') !== String(before[k] ?? '');
    if (changed) patch[k] = next[k];
  }
  if (patch.category_id != null && !('attributes' in patch)) patch.attributes = next.attributes;
  return patch;
}

/** Will this patch send the product back to review? Stock alone does not (D80). */
export const needsReview = (patch) => Object.keys(patch).some((k) => k !== 'id' && k !== 'qty');
